import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Bot,
  Check,
  CheckCircle2,
  Code2,
  Cpu,
  FileCode,
  FileSpreadsheet,
  FileText,
  FileType,
  Flame,
  Globe,
  Layers,
  Search,
  Sparkles,
  Terminal,
  Zap,
} from "lucide-react";

export const HermesAntiSlopShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Clean Anti-Slop Palette: Deep Forest Slate, Tactile Cream, Warm Amber, & Sage
  const bgDark = "#0A120E"; // Deep Slate Pine
  const sageGrid = "rgba(142, 168, 157, 0.08)";
  const tactileCream = "#F7F5EE"; // Clean textured paper feel
  const amberGold = "#F5A623"; // High-energy warm amber
  const neonLime = "#D8FF3E";
  const alertRed = "#E63946";
  const textWhite = "#FFFFFF";
  const textMuted = "#9EA9A2";
  const darkCard = "#111A15";

  // Spring Helper
  const sp = (delay: number, damping = 14, stiffness = 175) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 0.9 },
    });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgDark,
        color: textWhite,
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Precise Technical Grid (Zero Blurry Blobs) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, ${sageGrid} 1px, transparent 1px),
            linear-gradient(to bottom, ${sageGrid} 1px, transparent 1px)
          `,
          backgroundSize: "44px 44px",
          pointerEvents: "none",
        }}
      />

      {/* 2. Controlled Ambient Light Behind Content */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          right: "-10%",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(245, 166, 35, 0.08) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "10%",
          left: "-10%",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(216, 255, 62, 0.06) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      {/* 3. Top Header: Functional Brand & Telemetry Badge (Anti-Slop: No random clay spheres) */}
      <div
        style={{
          position: "absolute",
          top: 70,
          left: 65,
          right: 65,
          zIndex: 100,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Brand Pill */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "rgba(17, 26, 21, 0.92)",
            padding: "10px 22px",
            borderRadius: 30,
            border: "1px solid rgba(255, 255, 255, 0.14)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          <Terminal size={18} color={amberGold} />
          <span
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: textWhite,
            }}
          >
            HERMES AGENT
          </span>
        </div>

        {/* Real Engineering Telemetry Tag (Replacing random 3D clay balls!) */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "rgba(17, 26, 21, 0.92)",
            padding: "8px 18px",
            borderRadius: 20,
            border: "1px solid rgba(245, 166, 35, 0.25)",
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: neonLime }} />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 15,
              fontWeight: 700,
              color: textMuted,
            }}
          >
            LOCAL VENV · 0 TOKEN SLOP
          </span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SCENE 1: FRAMES 0 - 100 (0.00s - 3.33s) */}
      {/* "masih langganan AI satu-satu?" + Staggered Tactile Cards */}
      {/* ========================================================================= */}
      {frame >= 0 && frame < 100 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 230,
          }}
        >
          {/* Main Headline with Capsule */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              transform: `translateY(${interpolate(sp(0), [0, 1], [30, 0])}px)`,
              opacity: sp(0),
            }}
          >
            <span style={{ fontSize: 44, fontWeight: 800, color: textWhite, letterSpacing: "-0.01em" }}>
              masih langganan
            </span>
            <div
              style={{
                backgroundColor: "#16221B",
                border: "2px solid rgba(255, 255, 255, 0.18)",
                padding: "8px 28px",
                borderRadius: 24,
                transform: "rotate(-1.5deg)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
              }}
            >
              <span style={{ fontSize: 50, fontWeight: 900, color: amberGold }}>
                AI satu-satu?
              </span>
            </div>
          </div>

          {/* Staggered Tactile Receipt Cards (Clean & High-Contrast) */}
          <div
            style={{
              position: "relative",
              width: 860,
              marginTop: 55,
              display: "flex",
              flexDirection: "column",
              gap: 14,
              alignItems: "center",
            }}
          >
            {[
              { brand: "ChatGPT Plus", tag: "OPENAI", price: "±Rp330rb /bln", delay: 4, rot: -2.0 },
              { brand: "Claude Pro", tag: "ANTHROPIC", price: "±Rp330rb /bln", delay: 14, rot: 1.8 },
              { brand: "Gemini Advanced", tag: "GOOGLE", price: "±Rp309rb /bln", delay: 24, rot: -1.4 },
              { brand: "Midjourney", tag: "IMAGE GEN", price: "±Rp165rb /bln", delay: 34, rot: 1.6 },
            ].map((item, idx) => {
              const cardSp = sp(item.delay, 12, 190);
              const cardY = interpolate(cardSp, [0, 1], [-70, 0]);
              const cardScale = interpolate(cardSp, [0, 1], [0.88, 1]);

              if (frame < item.delay) return null;

              return (
                <div
                  key={idx}
                  style={{
                    width: "100%",
                    height: 94,
                    backgroundColor: tactileCream,
                    color: "#000000",
                    borderRadius: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 28px",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.55)",
                    transform: `translateY(${cardY}px) scale(${cardScale}) rotate(${item.rot}deg)`,
                    transformOrigin: "center center",
                    zIndex: idx + 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div
                      style={{
                        padding: "6px 14px",
                        backgroundColor: "#131C16",
                        borderRadius: 10,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 13,
                          fontWeight: 800,
                          color: amberGold,
                          letterSpacing: "0.06em",
                        }}
                      >
                        {item.tag}
                      </span>
                    </div>

                    <span style={{ fontSize: 26, fontWeight: 800, color: "#16201A" }}>
                      {item.brand}
                    </span>
                  </div>

                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 26,
                      fontWeight: 800,
                      color: alertRed,
                    }}
                  >
                    {item.price}
                  </span>
                </div>
              );
            })}

            {/* Bottom Total Anchor Card */}
            {frame >= 44 && (
              <div
                style={{
                  width: "100%",
                  height: 102,
                  backgroundColor: "#141F18",
                  border: `2px solid ${amberGold}`,
                  borderRadius: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 30px",
                  boxShadow: `0 0 35px rgba(245, 166, 35, 0.25)`,
                  transform: `scale(${sp(44)})`,
                  zIndex: 30,
                  marginTop: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Flame size={26} color={amberGold} />
                  <span style={{ fontSize: 24, fontWeight: 800, color: textWhite }}>
                    TOTAL BONCOS / BULAN:
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: "'Anton', sans-serif",
                    fontSize: 42,
                    color: amberGold,
                    letterSpacing: "0.03em",
                  }}
                >
                  ±Rp1.134.000 /bln
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 2: FRAMES 100 - 180 (3.33s - 6.00s) */}
      {/* "Setahun tembus 13 JUTAAN" + Strikethrough Shock */}
      {/* ========================================================================= */}
      {frame >= 100 && frame < 180 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 60px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              transform: `scale(${sp(100)})`,
            }}
          >
            <div style={{ fontSize: 60, fontWeight: 800, color: textWhite }}>
              Setahun bisa{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 82,
                  fontWeight: 400,
                  marginLeft: 6,
                }}
              >
                tembus
              </span>
            </div>
            <div
              style={{
                fontSize: 88,
                fontWeight: 900,
                color: amberGold,
                textShadow: "0 0 35px rgba(245, 166, 35, 0.35)",
                lineHeight: 1,
              }}
            >
              13 JUTAAN.
            </div>
          </div>

          {/* Strikethrough Price Presentation */}
          <div
            style={{
              marginTop: 65,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              transform: `scale(${sp(108)})`,
            }}
          >
            <div
              style={{
                fontSize: 114,
                fontWeight: 900,
                fontFamily: "'Anton', sans-serif",
                letterSpacing: "0.02em",
                color: amberGold,
                textShadow: "0 0 40px rgba(245, 166, 35, 0.4)",
                position: "relative",
                display: "inline-block",
                padding: "0 10px",
              }}
            >
              Rp13.600.000
              {/* Dynamic Magenta Strikethrough Line */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: "54%",
                  height: 10,
                  backgroundColor: "#FF2E93",
                  borderRadius: 6,
                  boxShadow: "0 0 20px #FF2E93",
                  width: `${interpolate(frame, [118, 142], [0, 100], {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  })}%`,
                }}
              />
            </div>

            <span
              style={{
                marginTop: 18,
                fontSize: 26,
                fontWeight: 600,
                color: textMuted,
                letterSpacing: "0.04em",
              }}
            >
              cuma buat bayar langganan web AI terpisah
            </span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 3: FRAMES 180 - 315 (6.00s - 10.50s) */}
      {/* "Di Hermes, semua model jadi satu terminal." (Real Models, Zero Hallucinations) */}
      {/* ========================================================================= */}
      {frame >= 180 && frame < 315 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 190,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              transform: `scale(${sp(180)})`,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 34, fontWeight: 700, color: textMuted }}>
              Solusi Cerdas Tanpa Boncos
            </span>
            <div style={{ fontSize: 56, fontWeight: 900, color: textWhite }}>
              Semua model{" "}
              <span style={{ color: amberGold }}>jadi satu tim.</span>
            </div>
          </div>

          {/* Unified AI Workforce Stack (Real Verified Models — Anti-Slop!) */}
          <div
            style={{
              width: 880,
              marginTop: 45,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {[
              { name: "Claude 3.7 Sonnet / Opus", role: "Arsitek Logika & Audit", active: true },
              { name: "GPT-4o & Codex Engine", role: "Backend, Automasi, Thread-Safe", active: true },
              { name: "DeepSeek R1 / Reasoning", role: "Riset Matematika & Riset Kompleks", active: true },
              { name: "Gemini 2.5 Flash / High", role: "Scraping Massal & Ingestion Cepat", active: false },
            ].map((m, idx) => (
              <div
                key={idx}
                style={{
                  width: "100%",
                  height: 84,
                  backgroundColor: m.active ? tactileCream : "#121C16",
                  color: m.active ? "#000000" : textWhite,
                  borderRadius: 18,
                  border: m.active ? "none" : "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 24px",
                  boxShadow: m.active ? "0 10px 24px rgba(0,0,0,0.5)" : "none",
                  transform: `scale(${sp(190 + idx * 8)})`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      backgroundColor: m.active ? "#131C16" : "rgba(255,255,255,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Bot size={22} color={m.active ? amberGold : textMuted} />
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 900 }}>{m.name}</div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: m.active ? "#4B5A51" : textMuted,
                      }}
                    >
                      {m.role}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: m.active ? "#131C16" : "transparent",
                    border: m.active ? "none" : "2px solid rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {m.active && <Check size={18} color={amberGold} strokeWidth={3} />}
                </div>
              </div>
            ))}

            {/* Overflow Badge */}
            <div
              style={{
                width: "100%",
                height: 72,
                borderRadius: 18,
                border: "2px dashed rgba(245, 166, 35, 0.4)",
                backgroundColor: "rgba(18, 28, 22, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 24px",
                transform: `scale(${sp(230)})`,
              }}
            >
              <span style={{ fontSize: 19, fontWeight: 700, color: textMuted }}>
                Bebas gonta-ganti model via local 9Router gateway
              </span>
              <div
                style={{
                  backgroundColor: amberGold,
                  color: "#000000",
                  padding: "6px 14px",
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 900,
                }}
              >
                +50 MODEL
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 4: FRAMES 315 - 395 (10.50s - 13.17s) */}
      {/* 2x2 Clean Capability Grid: Code, Research, Documents, Video */}
      {/* ========================================================================= */}
      {frame >= 315 && frame < 395 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 220,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 54,
              fontWeight: 900,
              transform: `scale(${sp(315)})`,
            }}
          >
            <span>Eksekusi</span>
            <div
              style={{
                backgroundColor: amberGold,
                color: "#000000",
                padding: "6px 22px",
                borderRadius: 18,
              }}
            >
              nyata,
            </div>
            <span>tanpa limit.</span>
          </div>

          {/* 2x2 Bento Capability Cards */}
          <div
            style={{
              width: 880,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 18,
              marginTop: 50,
            }}
          >
            {[
              {
                title: "Deep Research",
                sub: "Autonomous web search, 100+ sumber tervalidasi",
                tag: "OSINT ENGINE",
                icon: Search,
                color: amberGold,
              },
              {
                title: "Full-Stack Code",
                sub: "Bikin app, bot, script, debug otomatis di venv",
                tag: "TERMINAL LOCAL",
                icon: Code2,
                color: neonLime,
              },
              {
                title: "Otomasi Dokumen",
                sub: "Parse & generate Word, Excel, PPT, PDF sat-set",
                tag: "MULTI-FORMAT",
                icon: FileText,
                color: textWhite,
              },
              {
                title: "Video Production",
                sub: "Remotion Studio render video 9:16 + dynamic subtitle",
                tag: "BROADCAST QUALITY",
                icon: Layers,
                color: "#38BDF8",
              },
            ].map((c, idx) => {
              const IconComp = c.icon;
              return (
                <div
                  key={idx}
                  style={{
                    backgroundColor: tactileCream,
                    color: "#000000",
                    borderRadius: 24,
                    padding: "30px 24px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: 240,
                    boxShadow: "0 14px 34px rgba(0,0,0,0.6)",
                    transform: `scale(${sp(322 + idx * 8)})`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        backgroundColor: "#111A15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp size={28} color={c.color} />
                    </div>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#6E7D73",
                      }}
                    >
                      {c.tag}
                    </span>
                  </div>

                  <div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: "#111A15" }}>
                      {c.title}
                    </div>
                    <div style={{ fontSize: 16, color: "#4B5A51", fontWeight: 700, marginTop: 4 }}>
                      {c.sub}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 5: FRAMES 395 - 475 (13.17s - 15.83s) */}
      {/* Closing / Value Offer Card */}
      {/* ========================================================================= */}
      {frame >= 395 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 200,
            paddingBottom: 80,
            justifyContent: "space-between",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `scale(${sp(395)})`,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 32, fontWeight: 700, color: textMuted }}>
              Bukan Jualan Kelas · Murni Komunitas Belajar
            </span>
            <div style={{ fontSize: 58, fontWeight: 900, marginTop: 4 }}>
              Bangun tim AI lo <span style={{ color: amberGold }}>sendiri.</span>
            </div>
          </div>

          {/* Central Value Card */}
          <div
            style={{
              width: 880,
              backgroundColor: tactileCream,
              color: "#000000",
              borderRadius: 32,
              padding: "42px 40px",
              boxShadow: "0 25px 60px rgba(0,0,0,0.8)",
              transform: `scale(${sp(405)})`,
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 16,
                    backgroundColor: "#111A15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Terminal size={32} color={amberGold} />
                </div>
                <div>
                  <div style={{ fontSize: 30, fontWeight: 900 }}>HERMES AI ECOSYSTEM</div>
                  <div style={{ fontSize: 17, color: "#55645A", fontWeight: 700 }}>
                    100% Open Source · Jalan di Laptop Sendiri
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#111A15",
                  color: neonLime,
                  padding: "8px 18px",
                  borderRadius: 12,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 15,
                  fontWeight: 900,
                }}
              >
                GRATIS 100%
              </div>
            </div>

            <div style={{ height: 1, backgroundColor: "rgba(0,0,0,0.12)" }} />

            {/* Checklist */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                "Bebas pakai model apa aja tanpa langganan terpisah",
                "Eksekusi autonomous tugas riil langsung beres",
                "Sharing & belajar bareng di Grup Komunitas Telegram",
              ].map((txt, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: "#111A15",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Check size={16} color={amberGold} strokeWidth={3} />
                  </div>
                  <span style={{ fontSize: 21, fontWeight: 800, color: "#1D2821" }}>{txt}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Community CTA */}
          <div
            style={{
              width: 880,
              height: 94,
              backgroundColor: amberGold,
              color: "#000000",
              borderRadius: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              boxShadow: "0 14px 40px rgba(245, 166, 35, 0.4)",
              transform: `scale(${sp(420)})`,
            }}
          >
            <Globe size={36} color="#000000" />
            <span style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.01em" }}>
              GABUNG KOMUNITAS TELEGRAM DI BIO
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
